import React, { useEffect, useMemo, useState } from 'react';
import { Award, ChevronDown, ChevronRight, Gift, Phone, RefreshCw, Search, Share2, UserCheck, Users } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { getReferralDashboard } from '../api';
import { User } from '../types';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate: (id: string) => void;
}

type ReferralCandidate = {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  position_name: string;
  stage: string;
  stage_label: string;
  created_at: string;
  referrer_name: string;
  referral_code: string;
  referral_matched: boolean;
  incentive: { status: string; label: string; color: string };
};

type ReferralGroup = {
  referrer_id?: string;
  referrer_name: string;
  referrer_phone?: string;
  referral_code: string;
  referral_matched: boolean;
  total: number;
  interviewing: number;
  offer: number;
  onboarded: number;
  incentive_eligible: number;
  candidates: ReferralCandidate[];
};

type DashboardData = {
  summary: {
    total_referrers: number;
    total_candidates: number;
    interviewing: number;
    offer: number;
    onboarded: number;
    incentive_eligible: number;
    unmatched: number;
  };
  groups: ReferralGroup[];
};

const incentiveClass: Record<string, string> = {
  green: 'bg-green-50 text-green-700 border-green-100',
  amber: 'bg-amber-50 text-amber-700 border-amber-100',
  blue: 'bg-blue-50 text-blue-700 border-blue-100',
  indigo: 'bg-indigo-50 text-indigo-700 border-indigo-100',
  red: 'bg-red-50 text-red-700 border-red-100',
  gray: 'bg-gray-50 text-gray-600 border-gray-100',
};

const stageClass: Record<string, string> = {
  new: 'bg-gray-100 text-gray-700',
  screening: 'bg-indigo-100 text-indigo-700',
  screening_passed: 'bg-blue-100 text-blue-700',
  interview: 'bg-purple-100 text-purple-700',
  interview_passed: 'bg-green-100 text-green-700',
  offer: 'bg-amber-100 text-amber-700',
  pending_onboard: 'bg-orange-100 text-orange-700',
  onboarded: 'bg-emerald-100 text-emerald-700',
  rejected: 'bg-red-100 text-red-700',
  screening_rejected: 'bg-red-100 text-red-700',
};

export default function ReferralDashboard({ currentUser, notificationBell, onOpenCandidate }: Props) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const load = async () => {
    try {
      setLoading(true);
      const result = await getReferralDashboard();
      setData(result);
      const initial: Record<string, boolean> = {};
      result.groups.slice(0, 5).forEach((g: ReferralGroup) => { initial[g.referrer_id || g.referral_code] = true; });
      setExpanded(initial);
    } catch {
      toast.error('加载内推看板失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredGroups = useMemo(() => {
    const groups = data?.groups || [];
    const q = query.trim().toLowerCase();
    if (!q) return groups;
    return groups.map(g => ({
      ...g,
      candidates: g.candidates.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.position_name?.toLowerCase().includes(q) ||
        c.stage_label?.toLowerCase().includes(q) ||
        g.referrer_name?.toLowerCase().includes(q) ||
        g.referral_code?.toLowerCase().includes(q)
      ),
    })).filter(g => g.candidates.length > 0 || g.referrer_name?.toLowerCase().includes(q) || g.referral_code?.toLowerCase().includes(q));
  }, [data, query]);

  const cards = [
    { label: '内推人/码', value: data?.summary.total_referrers || 0, icon: Share2, color: 'text-primary-600 bg-primary-50' },
    { label: '内推简历', value: data?.summary.total_candidates || 0, icon: Users, color: 'text-indigo-600 bg-indigo-50' },
    { label: '面试中', value: data?.summary.interviewing || 0, icon: UserCheck, color: 'text-purple-600 bg-purple-50' },
    { label: 'Offer中', value: data?.summary.offer || 0, icon: Award, color: 'text-amber-600 bg-amber-50' },
    { label: '已入职/可核发', value: data?.summary.incentive_eligible || 0, icon: Gift, color: 'text-green-600 bg-green-50' },
  ];

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-hidden">
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-lg font-bold text-gray-900">内推看板</h1>
            <p className="text-sm text-gray-500 mt-0.5">统计所有内推码、内推人、候选人进展和激励达成情况</p>
          </div>
          <div className="flex items-center gap-3">
            {notificationBell}
            <button onClick={load} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white text-sm text-gray-600 hover:bg-gray-50">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />刷新
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {cards.map(card => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{card.label}</p>
                    <p className="text-xl font-bold text-gray-900 mt-1">{card.value}</p>
                  </div>
                  <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', card.color)}>
                    <Icon size={20} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-gray-800">内推码明细</h2>
              <p className="text-xs text-gray-400 mt-0.5">当前用户：{currentUser.display_name || currentUser.username}</p>
            </div>
            <div className="relative w-72 max-w-full">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="搜索内推码、内推人、候选人、手机号"
                className="w-full h-9 pl-9 pr-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>

          {loading ? (
            <div className="py-16 text-center text-gray-400">加载中...</div>
          ) : filteredGroups.length === 0 ? (
            <div className="py-16 text-center text-gray-400">暂无内推数据</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredGroups.map(group => {
                const key = group.referrer_id || group.referral_code;
                const isOpen = expanded[key];
                return (
                  <div key={key}>
                    <button
                      onClick={() => setExpanded(prev => ({ ...prev, [key]: !prev[key] }))}
                      className="w-full px-4 py-4 hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        {isOpen ? <ChevronDown size={17} className="text-gray-400" /> : <ChevronRight size={17} className="text-gray-400" />}
                        <div className="w-10 h-10 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center font-bold shrink-0">
                          {(group.referrer_name || group.referral_code).charAt(0)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold text-gray-900">{group.referrer_name}</span>
                            <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 text-xs">内推码：{group.referral_code}</span>
                            {!group.referral_matched && <span className="px-2 py-0.5 rounded bg-red-50 text-red-600 text-xs">未匹配系统用户</span>}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                            {group.referrer_phone && <span className="flex items-center gap-1"><Phone size={12} />{group.referrer_phone}</span>}
                            <span>内推 {group.total} 人</span>
                          </div>
                        </div>
                        <div className="hidden md:grid grid-cols-4 gap-2 text-center text-xs shrink-0 min-w-[360px]">
                          <div><p className="font-bold text-purple-600">{group.interviewing}</p><p className="text-gray-400">面试中</p></div>
                          <div><p className="font-bold text-amber-600">{group.offer}</p><p className="text-gray-400">Offer</p></div>
                          <div><p className="font-bold text-green-600">{group.onboarded}</p><p className="text-gray-400">已入职</p></div>
                          <div><p className="font-bold text-primary-600">{group.incentive_eligible}</p><p className="text-gray-400">可核发</p></div>
                        </div>
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-4 pb-4 overflow-x-auto">
                        <table className="w-full text-sm border border-gray-100 rounded-lg overflow-hidden">
                          <thead className="bg-gray-50 text-xs text-gray-500">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium">候选人</th>
                              <th className="text-left px-3 py-2 font-medium">手机号</th>
                              <th className="text-left px-3 py-2 font-medium">岗位</th>
                              <th className="text-left px-3 py-2 font-medium">面试进展</th>
                              <th className="text-left px-3 py-2 font-medium">激励情况</th>
                              <th className="text-left px-3 py-2 font-medium">投递时间</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {group.candidates.map(candidate => (
                              <tr key={candidate.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <button onClick={() => onOpenCandidate(candidate.id)} className="font-medium text-primary-600 hover:underline">
                                    {candidate.name}
                                  </button>
                                  {candidate.email && <div className="text-xs text-gray-400 mt-0.5">{candidate.email}</div>}
                                </td>
                                <td className="px-3 py-2 text-gray-700">{candidate.phone || '-'}</td>
                                <td className="px-3 py-2 text-gray-700">{candidate.position_name}</td>
                                <td className="px-3 py-2">
                                  <span className={clsx('px-2 py-1 rounded-full text-xs font-medium', stageClass[candidate.stage] || 'bg-gray-100 text-gray-600')}>
                                    {candidate.stage_label}
                                  </span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className={clsx('px-2 py-1 rounded-full text-xs font-medium border', incentiveClass[candidate.incentive.color] || incentiveClass.gray)}>
                                    {candidate.incentive.label}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{candidate.created_at?.slice(0, 10) || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
